import { useId, useState } from "react";
import UiIcon from "../UiIcon";
import RatingScale, { RatingValue } from "./RatingScale";
import { routineCopy } from "./routineCopy";
import { formatDate } from "./routineModel";
import { responsibilityPanelState } from "./evaluationResponsibility";
import { withPercent } from "../../i18n/number-format";
import { evaluationResponsibilityCopy, formatErDate } from "./evaluationResponsibilityCopy";

const DUTY_TONE = { OVERDUE: "danger", DUE_SOON: "warning", OPEN: "info", ON_TIME: "success", COMPLETED_LATE: "warning" };
const STATE_ICON = {
    loading: "schedule", unavailable: "warning", verification: "shield", not_applicable: "visibility",
    exempt: "flag", pending: "schedule", evaluated: "check", stale: "warning",
};

// Period-level Core Responsibilities criterion. Everything shown comes from the evidence endpoint
// (`data`); the panel never infers applicability, metrics or completion. Rating goes through
// `onRate(levelId)`, which must reject on failure so the error is reported here.
export default function EvaluationResponsibilityPanel({
    data = null, loading = false, error = null, onRetry = null, readOnly = null,
    routineLevels = [], onRate = null, language = "en", orgLabel = text => text, nameOf = () => null,
}) {
    const c = routineCopy(language).responsibility;
    const headingId = useId();
    const [save, setSave] = useState({ state: "idle", message: "" });
    const panel = responsibilityPanelState({ loading, error, data });
    const { kind, metrics, assessment } = panel;
    const rated = ["pending", "evaluated", "stale"].includes(kind);
    // The backend also says whether this viewer may rate (direct manager, active period).
    const editable = rated && panel.canRate && data?.viewer?.canRate !== false && !readOnly && typeof onRate === "function";
    const [showDuties, setShowDuties] = useState(false);
    const duties = kind === "loading" || kind === "unavailable" ? [] : data?.ledger?.items || [];
    const d = evaluationResponsibilityCopy(language);
    const levelName = id => {
        const level = routineLevels.find(l => l.id === id);
        return level ? orgLabel(level.label) : null;
    };
    const percent = value => withPercent(new Intl.NumberFormat(language === "fr" ? "fr-CA" : "en-CA", { maximumFractionDigits: 1 }).format(value), language);
    const ratedOn = formatDate(assessment?.evaluatedAt, language);

    const rate = async levelId => {
        setSave({ state: "saving", message: "" });
        try { await onRate(levelId); setSave({ state: "saved", message: "" }); }
        catch (e) { setSave({ state: "failed", message: e?.message || c.saveFailed }); }
    };

    return <section className="cvi-routine-card cvi-er" aria-labelledby={headingId} aria-busy={kind === "loading" || undefined}>
        <header className="cvi-er-head">
            <div className="cvi-er-heading">
                <span className="cvi-routine-eyebrow">{c.kicker}</span>
                <h3 id={headingId}>{c.title}</h3>
                <p>{c.intro}</p>
            </div>
            <div className="cvi-er-tags">
                <span className="cvi-er-importance">{c.importance}</span>
                <span className={`cvi-er-state is-${kind}`}><UiIcon name={STATE_ICON[kind]} size={13} />{c.state[kind]}</span>
            </div>
        </header>

        {kind === "loading" && <div className="cvi-er-message"><div className="cvi-routine-skeleton" aria-hidden="true"><span /><span /></div><p className="cvi-routine-muted">{c.loading}</p></div>}

        {kind === "unavailable" && <div className="cvi-er-message is-warning" role="alert">
            <p>{c.unavailable}</p>
            {onRetry && <button type="button" className="adm-btn adm-btn--sm" onClick={onRetry}>{c.retry}</button>}
        </div>}

        {kind === "verification" && <div className="cvi-er-message" role="status">
            <p>{c.verification}</p>
            {panel.pendingSources > 0 && <p className="cvi-er-detail">{c.verificationSources(panel.pendingSources)}</p>}
            {assessment && <p className="cvi-er-detail">{c.recordedWhileGated(levelName(assessment.levelId) || "—", ratedOn)}</p>}
        </div>}

        {(kind === "not_applicable" || kind === "exempt") && <div className="cvi-er-message" role="status">
            <p>{kind === "exempt" ? c.exempt : c.notApplicable}</p>
            <p className="cvi-er-detail">{c.effectiveWeightZero}</p>
        </div>}

        {rated && <>
            <dl className="cvi-er-metrics">
                <div><dt>{c.metrics.assigned}</dt><dd>{metrics.assignedWorkload ?? "—"}</dd>
                    {metrics.assignedWorkload != null && <small>{c.metrics.assignedHelp(metrics.assignedWorkload)}</small>}</div>
                <div><dt>{c.metrics.completion}</dt><dd>{metrics.completionRate == null ? "—" : percent(metrics.completionRate)}</dd>
                    <small>{metrics.dueCount ? c.metrics.completionHelp(metrics.onTimeCount ?? 0, metrics.dueCount) : c.metrics.completionNone}</small></div>
                <div className={metrics.overdue > 0 ? "is-attention" : ""}><dt>{c.metrics.overdue}</dt><dd>{metrics.overdue ?? "—"}</dd>
                    <small>{metrics.completedLate > 0 ? c.metrics.lateHelp(metrics.completedLate) : c.metrics.overdueHelp}</small></div>
            </dl>
            <div className="cvi-er-rating">
                <div className="cvi-routine-row">
                    <span className="cvi-routine-label">{c.yourRating}</span>
                    {editable
                        ? <RatingScale levels={routineLevels} value={assessment?.levelId || null} tone="manager" orgLabel={orgLabel}
                            disabled={save.state === "saving"} busy={save.state === "saving"} label={`${c.yourRating} · ${c.title}`} onChange={rate} />
                        : <RatingValue levels={routineLevels} levelId={assessment?.levelId} orgLabel={orgLabel} emptyText={routineCopy(language).review.notRated} />}
                    <span className={`cvi-routine-save is-${save.state}`} aria-live="polite">
                        {save.state === "saving" ? c.saving : save.state === "saved" ? c.saved : ""}</span>
                </div>
                <p className="cvi-er-detail">
                    {kind === "evaluated" ? c.evaluated(assessment?.evaluatorId ? nameOf(assessment.evaluatorId) : null, ratedOn)
                        : kind === "stale" ? c.stale(ratedOn)
                        : readOnly === "closed" ? c.readOnlyClosed : readOnly ? c.readOnlyView : panel.canRate ? c.pending : c.cannotRate}
                </p>
                {save.state === "failed" && <div className="adm-callout is-danger" role="alert"><UiIcon name="warning" size={15} /><div>{save.message}</div></div>}
            </div>
        </>}

        {/* Where the figures come from: each registered duty with its source, deadline and state. */}
        {duties.length > 0 && <div className="cvi-er-duties">
            <button type="button" className="adm-btn adm-btn--sm adm-btn--ghost" aria-expanded={showDuties} aria-controls={`${headingId}-duties`}
                onClick={() => setShowDuties(open => !open)}>
                <span className={`cvi-er-chevron${showDuties ? " is-open" : ""}`} aria-hidden="true"><UiIcon name="chevronDown" size={14} /></span>
                {showDuties ? d.hideDuties : d.showDuties(duties.length)}
            </button>
            {showDuties && <ul id={`${headingId}-duties`} className="cvi-er-duty-list">
                {duties.map(duty => <li key={duty.id}>
                    <span className="cvi-er-duty-main">
                        <strong>{d.sources[duty.sourceType] || duty.sourceType}</strong>
                        <small>{[duty.subjectName && d.forPerson(duty.subjectName), duty.label, duty.revision > 0 && d.revision(duty.revision), duty.transferredFrom && d.transferred].filter(Boolean).join(" · ")}</small>
                    </span>
                    <span className="cvi-er-duty-date">{duty.completedAt ? d.completed(formatErDate(duty.completedAt, language)) : d.due(formatErDate(duty.dueAt, language))}</span>
                    <span className={`adm-badge${DUTY_TONE[duty.state] ? ` adm-badge--${DUTY_TONE[duty.state]}` : ""}`}>{d.states[duty.state] || duty.state}</span>
                </li>)}
            </ul>}
        </div>}
    </section>;
}
